import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser, isAdmin, isAdvisor, isInvestmentBanker } from '../services/authService';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import useInactivityTimer from '../hooks/useInactivityTimer';

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuthenticated, setAdminStatus } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userData = await loginUser(username, password);

      setAuthenticated(true);
      setAdminStatus(isAdmin());

      toast.success('Login successful!');

      // The inactivity timer will be set up automatically by the hook
      // when the user is redirected to the home page
      const isAdminUser = isAdmin();
      const isAdvisorUser = isAdvisor();
      const isIBUser = isInvestmentBanker();

      if (!isAdminUser && !isAdvisorUser && !isIBUser) {
        navigate('/user-dashboard');
      } else {
        navigate('/home');
      }


    } catch (error) {
      setError(error.message || 'Login failed. Please check your credentials.');
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 transition-all duration-300 ease-out transform origin-center hover:-translate-y-1 hover:scale-[1.01] hover:shadow-xl hover:border-gray-200 relative"
      onSubmit={handleSubmit}
    >
      <div className="mb-4 text-center">
        <img
          src="/capgemini-logo.png"
          alt="Capgemini"
          className="h-8 mx-auto mb-3 object-contain"
        />
        <div className="inline-block">
          <img src="/a3-logo.png" alt="A3" className="h-10 mx-auto object-contain" />
          <p className="mt-0.5 text-xs text-primary-light italic">Assist, Augment, Autonomize</p>
        </div>
      </div>

      <h2 className="text-xl font-bold text-primary text-center mb-5">Login</h2>

      <div className="mb-4">
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
          Username
        </label>
        <input
          id="username"
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          disabled={isLoading}
          className="w-full py-2.5 px-3 border border-gray-300 rounded-md text-sm transition-all focus:outline-none focus:border-black focus:shadow-sm focus:ring-2 focus:ring-blue-200 focus:-translate-y-0.5"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password
        </label>
        <input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          disabled={isLoading}
          className="w-full py-2.5 px-3 border border-gray-300 rounded-md text-sm transition-all focus:outline-none focus:border-black focus:shadow-sm focus:ring-2 focus:ring-blue-200 focus:-translate-y-0.5"
        />
      </div>

      <div className="text-right mb-5">
        <Link to="/forgot-password" className="text-sm text-primary hover:text-primary-light hover:underline transition-colors">
          Forgot password?
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-2.5 rounded-md mb-5 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        className="w-full py-2.5 bg-primary text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-primary-light flex justify-center items-center relative overflow-hidden"
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="inline-block w-5 h-5 border-2 border-white/30 rounded-full border-t-white animate-spin"></span>
        ) : (
          'Log In'
        )}
      </button>

      <div className="mt-5 text-center text-sm text-gray-600">
        <p>Don't have an account? <Link to="/a3-signup" className="text-primary font-medium hover:text-primary-light hover:underline transition-colors">Sign up</Link></p>
      </div>
    </form>
  );
};

export default LoginForm;